import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { Control } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import Select from "@/components/FormControl/Select/Select";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import UsersIcon from "@/components/SVGComponents/UsersIcon";
import { combineClasses, requiredMessage } from "@/lib/utils";
import toast from "@/lib/toast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChapters } from "@/store/slices/chapterSlice";
import chapterService from "@/services/api/chapters";
import type { Chapter } from "@/services/api/chapters";
import userService from "@/services/api/user";
import type { User } from "@/services/api/user";
import { getDisplayName } from "@/lib/user";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { SelectOption } from "@/pages/CreateGathering/lib/cityOptions";
import styles from "./OpenChapterModal.module.scss";

const LEAD_QUERY_BASE = { role: "ADMIN", screenType: "management"};

// getDisplayName falls back to the email when there's no name, so only
// append the email when it isn't already the label.
const getLeadLabel = (user: User) => {
  const name = getDisplayName(user);
  return user.email && name !== user.email ? `${name} - ${user.email}` : name;
};

const toLeadOption = (user: User): SelectOption => ({ label: getLeadLabel(user), value: String(user.id) });

const cityField: FormConfig = {
  city: {
    controlType: "input",
    label: "City",
    isRequired: true,
    labelVariant: "caption",
    placeholder: "e.g. Hyderabad",
    validations: {
      required: requiredMessage("City"),
      maxLength: { value: 80, message: "City must be under 80 characters" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
};

interface OpenChapterFormValues {
  city: string;
  chapterLead: SelectOption | null;
}

export interface OpenChapterModalProps {
  isOpen: boolean;
  onClose: () => void;
  chapter?: Chapter | null;
}

const OpenChapterModal = ({ isOpen, onClose, chapter = null }: OpenChapterModalProps) => {
  const dispatch = useAppDispatch();
  const chapters = useAppSelector((state) => state.chapter.chapters);
  const isEditing = chapter !== null;

  const { register, handleSubmit, watch, control, reset, getValues, setValue, formState } =
    useForm<OpenChapterFormValues>({
      defaultValues: { city: "", chapterLead: null },
    });

  useEffect(() => {
    if (!isOpen) return;
    const leadName = chapter?.leadUser ?? null;
    reset({
      city: chapter?.name ?? "",
      chapterLead: leadName ? { label: leadName, value: "" } : null,
    });
    if (!leadName) return;

    // GET /chapters only returns the lead's display name, so look the user
    // up to show their email (and get their id) in the pre-selected value.
    let cancelled = false;
    userService
      .getUsers({ search: leadName, pageNo: 1, perPage: 10, ...LEAD_QUERY_BASE })
      .then((res) => {
        if (cancelled) return;
        const normalizedLead = leadName.trim().toLowerCase();
        const match = (res.data?.results ?? []).find(
          (user) => getDisplayName(user).trim().toLowerCase() === normalizedLead,
        );
        // Don't clobber a lead the admin already picked while this was loading.
        if (match && getValues("chapterLead")?.value === "") {
          setValue("chapterLead", toLeadOption(match));
        }
      })
      .catch(() => {
        // Keep the name-only fallback if the lookup fails.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chapter]);

  const cityInput = watch("city");

  const [leadSearch, setLeadSearch] = useState("");
  const debouncedLeadSearch = useDebouncedValue(leadSearch);
  const [leadOptions, setLeadOptions] = useState<SelectOption[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setIsLoadingLeads(true);

    userService
      .getUsers({ search: debouncedLeadSearch || undefined, pageNo: 1, perPage: 10, ...LEAD_QUERY_BASE })
      .then((res) => {
        if (cancelled) return;
        setLeadOptions((res.data?.results ?? []).map(toLeadOption));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLeads(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedLeadSearch]);

  const matchedChapter = chapters.find(
    (candidate) => candidate.name.trim().toLowerCase() === cityInput.trim().toLowerCase(),
  );

  const handleClose = () => {
    reset();
    setLeadSearch("");
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEditing && chapter) {
        await chapterService.updateChapter({
          chapterId: chapter.id,
          chapterName: values.city,
          ...(values.chapterLead && values.chapterLead.value ? { leadUser: Number(values.chapterLead.value) } : {}),
        });
        toast.success("Chapter updated.");
      } else {
        await chapterService.createChapter({
          chapterName: values.city,
          leadUser: values.chapterLead ? Number(values.chapterLead.value) : 0,
        });
        toast.success("Chapter created.");
      }
      dispatch(getChapters({ pageNo: 1, perPage: 100 }));
      handleClose();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Manage chapter" : "Open a chapter"}
      subtitle={
        isEditing
          ? "Update this chapter's city or chapter lead."
          : "A chapter stays hidden from members until it goes live."
      }
      onSubmit={onSubmit}
      submitLabel={isEditing ? "Save changes" : "Create chapter"}
      submitIcon={<LocationPinIcon width={14} height={14} />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.openChapterModal)}
    >
      {formRenderer<OpenChapterFormValues>(cityField, formState, register)}

      <Select
        name="chapterLead"
        control={control as Control<any>}
        label="Chapter lead"
        isRequired={true}
        labelVariant="caption"
        placeholder="Search members…"
        className={combineClasses(styles.fieldSelect)}
        options={leadOptions}
        isDisabled={formState.isSubmitting}
        validations={isEditing ? {} : { required: "Chapter lead is required" }}
        onInputChange={(value) => setLeadSearch(value)}
        noOptionsMessage={() => (isLoadingLeads ? "Searching…" : leadSearch ? "No matching members" : "Start typing to search")}
      />

      {matchedChapter && matchedChapter.waitlistCount > 0 && (
        <div className={styles.infoBanner}>
          <UsersIcon className={styles.infoBannerIcon} />
          <p>
            {matchedChapter.waitlistCount} people are already on the {matchedChapter.name} waitlist and will be
            notified when the chapter goes live.
          </p>
        </div>
      )}
    </FormModal>
  );
};

export default OpenChapterModal;
